<template>
    <div class="ViewMapByName-MAIN">
        <div
            v-show="showMap"
            ref="mapEl"
            class="map"
        ></div>

        <img
            v-if="searchFinished && !showMap"
            src="../assets/mapa-base.png"
            class="map-base"
            alt="No se encontró ninguna promoción cerca"
        />

        <p v-if="loading" class="map-status">
            Buscando ubicaciones cercanas...
        </p>
    </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";

export default {
    name: "ViewMapByName",

    props: {
        apiKey: {
            type: String,
            required: true
        },

        // Ejemplo: "Cinemex"
        searchTerm: {
            type: String,
            required: true
        },

        radius: {
            type: Number,
            default: 5000
        },

        zoom: {
            type: Number,
            default: 13
        },

        maxResults: {
            type: Number,
            default: 20
        },

        fallbackCenter: {
            type: Object,
            default: () => ({
                lat: 19.432608,
                lng: -99.133209
            })
        }
    },

    data() {
        return {
            map: null,
            infoWindow: null,

            userMarker: null,
            userCircle: null,
            placeMarkers: [],

            currentCenter: null,

            loading: true,
            showMap: false,
            searchFinished: false,

            searchToken: 0,

            dictionary: {
                chilis: "chili's",
                cinepolis: "cinépolis"
            }
        };
    },

    watch: {
        searchTerm(newValue, oldValue) {
            if (
                newValue !== oldValue &&
                this.currentCenter &&
                this.map
            ) {
                this.searchPlaces(this.currentCenter, newValue);
            }
        },

        radius(newValue) {
            if (!this.currentCenter || !this.map) return;

            if (this.userCircle) {
                this.userCircle.setRadius(newValue);
                this.fitMapToRadius();
            }

            this.searchPlaces(
                this.currentCenter,
                this.searchTerm
            );
        }
    },

    methods: {
        async loadGoogle() {
            const loader = new Loader({
                apiKey: this.apiKey,
                version: "quarterly"
            });

            return await loader.load();
        },

        normalizedTerm(term) {
            const normalized = String(term || "")
                .trim()
                .toLowerCase();

            return this.dictionary[normalized] || normalized;
        },

        normalizeForCompare(value) {
            return String(value || "")
                .toLowerCase()
                .trim()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9\s]/g, " ")
                .replace(/\s+/g, " ")
                .trim();
        },

        matchesBrand(placeName, searchTerm) {
            const name = this.normalizeForCompare(placeName);
            const term = this.normalizeForCompare(searchTerm);

            if (!name || !term) return false;

            const stopWords = new Set([
                "la",
                "el",
                "los",
                "las",
                "de",
                "del",
                "y",
                "the",
                "a",
                "an",
                "hotel"
            ]);

            const tokens = term
                .split(" ")
                .filter(token => token && !stopWords.has(token));

            if (!tokens.length) return false;

            return name.includes(tokens.join(" "));
        },

        distanceMeters(a, b) {
            const earthRadius = 6371000;
            const toRadians = value =>
                (value * Math.PI) / 180;

            const dLat = toRadians(b.lat - a.lat);
            const dLng = toRadians(b.lng - a.lng);

            const lat1 = toRadians(a.lat);
            const lat2 = toRadians(b.lat);

            const haversine =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(lat1) *
                    Math.cos(lat2) *
                    Math.sin(dLng / 2) ** 2;

            return (
                2 *
                earthRadius *
                Math.asin(Math.sqrt(haversine))
            );
        },

        getPlaceName(place) {
            if (typeof place?.displayName === "string") {
                return place.displayName;
            }

            return place?.displayName?.text || "";
        },

        getPlaceCoordinates(place) {
            if (!place?.location) return null;

            const lat =
                typeof place.location.lat === "function"
                    ? place.location.lat()
                    : place.location.lat;

            const lng =
                typeof place.location.lng === "function"
                    ? place.location.lng()
                    : place.location.lng;

            if (
                !Number.isFinite(lat) ||
                !Number.isFinite(lng)
            ) {
                return null;
            }

            return { lat, lng };
        },

        clearPlaceMarkers() {
            this.placeMarkers.forEach(marker => {
                marker.setMap(null);
            });

            this.placeMarkers = [];
        },

        async initMap(center) {
            await this.loadGoogle();

            const google = window.google;

            this.currentCenter = center;

            this.map = new google.maps.Map(
                this.$refs.mapEl,
                {
                    center,
                    zoom: this.zoom,

                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,

                    gestureHandling: "none",
                    zoomControl: false,
                    keyboardShortcuts: false
                }
            );

            this.infoWindow =
                new google.maps.InfoWindow();

            this.renderUserPosition(center);
            this.fitMapToRadius();

            await this.searchPlaces(
                center,
                this.searchTerm
            );
        },

        renderUserPosition(center) {
            const google = window.google;

            this.userMarker?.setMap(null);
            this.userCircle?.setMap(null);

            this.userMarker = new google.maps.Marker({
                map: this.map,
                position: center,
                title: "Mi ubicación",

                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 9,
                    fillColor: "#4285F4",
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: "#ffffff"
                }
            });

            this.userCircle = new google.maps.Circle({
                map: this.map,
                center,
                radius: this.radius,

                fillColor: "#4285F4",
                fillOpacity: 0.12,

                strokeColor: "#4285F4",
                strokeOpacity: 0.6,
                strokeWeight: 2,

                clickable: false
            });
        },

        fitMapToRadius() {
            if (!this.map || !this.userCircle) return;

            const bounds = this.userCircle.getBounds();

            if (bounds) {
                this.map.fitBounds(bounds, 40);
            }
        },

        async searchPlaces(center, term) {
            const keyword = this.normalizedTerm(term);

            if (!this.map || !keyword) {
                this.loading = false;
                this.searchFinished = true;
                this.showMap = false;
                return;
            }

            const token = ++this.searchToken;

            this.loading = true;
            this.searchFinished = false;
            this.showMap = true;

            this.clearPlaceMarkers();

            try {
                const google = window.google;

                const { Place } =
                    await google.maps.importLibrary(
                        "places"
                    );

                const response =
                    await Place.searchByText({
                        textQuery: keyword,

                        /*
                         * Es una preferencia geográfica.
                         * Después se aplica un filtro estricto
                         * por distancia.
                         */
                        locationBias: {
                            center,
                            radius: this.radius
                        },

                        maxResultCount: Math.min(
                            Math.max(this.maxResults, 1),
                            20
                        ),

                        fields: [
                            "displayName",
                            "location",
                            "formattedAddress",
                            "googleMapsURI"
                        ]
                    });

                if (token !== this.searchToken) return;

                const places = response?.places || [];

                const validPlaces = places
                    .map(place => {
                        const location =
                            this.getPlaceCoordinates(place);

                        if (!location) return null;

                        const name =
                            this.getPlaceName(place);

                        const distance =
                            this.distanceMeters(
                                center,
                                location
                            );

                        /*
                         * Este filtro hace que ningún pin
                         * quede fuera del círculo.
                         */
                        if (distance > this.radius) {
                            return null;
                        }

                        if (
                            !this.matchesBrand(
                                name,
                                keyword
                            )
                        ) {
                            return null;
                        }

                        return {
                            place,
                            name,
                            location,
                            distance
                        };
                    })
                    .filter(Boolean)
                    .sort(
                        (a, b) =>
                            a.distance - b.distance
                    );

                if (validPlaces.length === 0) {
                    this.showMap = false;
                    return;
                }

                this.showMap = true;

                validPlaces.forEach(result => {
                    this.createPlaceMarker(result);
                });

                /*
                 * No hacemos fitBounds con los markers,
                 * porque queremos mantener visible todo
                 * el círculo del radio.
                 */
                this.$nextTick(() => {
                    this.fitMapToRadius();
                });
            } catch (error) {
                console.error(
                    "Error buscando ubicaciones:",
                    error
                );

                this.showMap = false;
            } finally {
                if (token === this.searchToken) {
                    this.loading = false;
                    this.searchFinished = true;
                }
            }
        },

        createPlaceMarker(result) {
            const google = window.google;

            const {
                place,
                name,
                location,
                distance
            } = result;

            const marker = new google.maps.Marker({
                map: this.map,
                position: location,
                title: name || this.searchTerm
            });

            marker.addListener("click", () => {
                const mapsLink = place.googleMapsURI
                    ? `
                        <br/>
                        <a
                            href="${place.googleMapsURI}"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Ver en Google Maps
                        </a>
                    `
                    : "";

                this.infoWindow.setContent(`
                    <div style="min-width:220px">
                        <strong>
                            ${name || this.searchTerm}
                        </strong>
                        <br/>

                        ${place.formattedAddress || ""}
                        <br/>

                        ${Math.round(distance)} m

                        ${mapsLink}
                    </div>
                `);

                this.infoWindow.open(
                    this.map,
                    marker
                );
            });

            this.placeMarkers.push(marker);
        },

        getLocation() {
            const useCenter = center => {
                this.initMap(center);
            };

            if (!("geolocation" in navigator)) {
                useCenter(this.fallbackCenter);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                position => {
                    useCenter({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                error => {
                    console.warn(
                        "No se pudo obtener la ubicación:",
                        error
                    );

                    useCenter(this.fallbackCenter);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000
                }
            );
        }
    },

    mounted() {
        this.getLocation();
    },

    beforeUnmount() {
        this.searchToken++;

        this.clearPlaceMarkers();

        this.userMarker?.setMap(null);
        this.userCircle?.setMap(null);

        this.map = null;
    }
};
</script>

<style scoped>
.ViewMapByName-MAIN {
    width: 100%;
    position: relative;
}

.map,
.map-base {
    width: 100%;
    height: 300px;
}

.map {
    border: 0;
}

.map-base {
    object-fit: contain;
    display: block;
}

.map-status {
    width: 100%;
    padding: 18px;
    margin: 0;
    box-sizing: border-box;
    text-align: center;
}
</style>
